import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Eye, 
  Database, 
  CreditCard, 
  Share2, 
  Cookie, 
  UserCheck, 
  ShieldAlert, 
  ArrowLeft,
  ChevronRight,
  Mail
} from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('collect');

  // Scroll spy for sidebar navigation
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (window.scrollY >= sectionTop - 150) {
          current = section.getAttribute('id');
        }
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 120,
        behavior: 'smooth'
      });
      setActiveSection(id);
    }
  };

  const LastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-blue-500 selection:text-white">
      
      {/* --- NAVBAR --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2">
                <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
                <span className="font-bold text-lg text-white tracking-tight hidden md:block">Hollowpad</span>
             </div>
             <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block"></div>
             <span className="font-medium text-slate-400 text-sm">Legal Center</span>
          </div>

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-all bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-800"
          >
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Back to App</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20">
        
        {/* Header */}
        <div className="mb-12 border-b border-slate-800 pb-8">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">Privacy Policy</h1>
            <p className="text-lg text-slate-400 max-w-2xl">
                Your privacy is critical to us. This policy outlines how Hollowpad collects, uses, and protects your personal data 
                while you use our AI generation tools.
            </p>
            <p className="mt-4 text-sm text-slate-500 font-mono">
                Last Updated: <span className="text-slate-300">{LastUpdated}</span>
            </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* --- SIDEBAR NAVIGATION (Sticky) --- */}
            <aside className="hidden lg:block lg:col-span-3">
                <div className="sticky top-28 space-y-1">
                    <p className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Policy Sections</p>
                    <nav className="space-y-1">
                        <NavLink id="collect" icon={Eye} label="1. Information We Collect" active={activeSection} onClick={scrollTo} />
                        <NavLink id="usage" icon={Database} label="2. How We Use Data" active={activeSection} onClick={scrollTo} />
                        <NavLink id="payments" icon={CreditCard} label="3. Payment Information" active={activeSection} onClick={scrollTo} />
                        <NavLink id="ai" icon={Share2} label="4. AI & Third Parties" active={activeSection} onClick={scrollTo} />
                        <NavLink id="storage" icon={Lock} label="5. Security & Storage" active={activeSection} onClick={scrollTo} />
                        <NavLink id="cookies" icon={Cookie} label="6. Cookies" active={activeSection} onClick={scrollTo} />
                        <NavLink id="rights" icon={UserCheck} label="7. Your Rights" active={activeSection} onClick={scrollTo} />
                        <NavLink id="contact" icon={Mail} label="8. Contact Us" active={activeSection} onClick={scrollTo} />
                    </nav>
                </div>
            </aside>

            {/* --- MAIN CONTENT --- */}
            <main className="lg:col-span-9 space-y-16">
                
                {/* 1. Information Collection */}
                <Section id="collect" title="1. Information We Collect">
                    <p>
                        We collect specific types of information to provide and improve our services:
                    </p>
                    <ul className="list-disc pl-5 space-y-3 text-slate-400 mt-4">
                        <li>
                            <strong>Account Information:</strong> When you register via Firebase Authentication, we collect your email address, 
                            username, and profile picture.
                        </li>
                        <li>
                            <strong>Usage Data:</strong> We track your interactions with the platform, including the number of credits used, 
                            generation timestamps, and feature usage to calculate your virtual credit balance.
                        </li>
                        <li>
                            <strong>Input Data:</strong> We store the prompts and parameters you input into our AI tools to generate assets 
                            (e.g., text descriptions for code generation).
                        </li>
                    </ul>
                </Section>

                {/* 2. Usage */}
                <Section id="usage" title="2. How We Use Your Data">
                    <p>
                        We utilize your data for the following purposes:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 my-4">
                        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
                            <span className="text-slate-200 font-bold block mb-1">Service Provision</span>
                            <span className="text-sm text-slate-500">
                                To authenticate you, manage your credit balance, and execute the AI generation tasks you request.
                            </span>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
                            <span className="text-slate-200 font-bold block mb-1">Billing & Notifications</span>
                            <span className="text-sm text-slate-500">
                                To process subscriptions via Paystack and send transactional emails (e.g., receipts, password resets).
                            </span>
                        </div>
                    </div>
                    <p>
                        We do <strong>not</strong> sell your personal data to advertisers or third-party data brokers.
                    </p>
                </Section>

                {/* 3. Payments */}
                <Section id="payments" title="3. Payment Information">
                    <div className="flex items-start gap-4 bg-blue-900/10 border border-blue-900/30 p-4 rounded-lg">
                        <ShieldAlert className="text-blue-500 shrink-0 mt-1" size={20} />
                        <div className="text-sm">
                            <strong className="text-blue-300 block mb-1">PCI-DSS Compliance</strong>
                            Hollowpad does <strong>not</strong> store your credit card numbers or banking details on our servers.
                        </div>
                    </div>
                    <p className="mt-4">
                        All payments are processed securely by <strong>Paystack</strong>. We only retain:
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-slate-400 mt-2">
                        <li>A masked reference to your payment method (e.g., last 4 digits).</li>
                        <li>Transaction Reference IDs and Authorization Codes to manage subscriptions and refunds.</li>
                        <li>Your billing email address associated with the transaction.</li>
                    </ul>
                </Section>

                {/* 4. AI & Third Parties */}
                <Section id="ai" title="4. AI Providers & Third-Party Sharing">
                    <p>
                        To provide AI generation capabilities, we act as an intermediary between you and Large Language Model (LLM) providers 
                        (such as Google, OpenAI or Anthropic).
                    </p>
                    <p>
                        <strong>Data Transmission:</strong> When you generate content, your prompt text is sent securely to these third-party APIs. 
                        We do not send your personal identifiers (email/name) to AI providers unless strictly necessary for safety moderation.
                    </p>
                    <p>
                        <strong>External Links:</strong> Our platform may contain links to third-party sites (e.g., Roblox). 
                        We are not responsible for the privacy practices of those sites.
                    </p>
                </Section>

                {/* 5. Security */}
                <Section id="storage" title="5. Security & Data Storage">
                    <p>
                        We utilize industry-standard security measures to protect your data:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-slate-400 mt-4">
                        <li>
                            <strong>Encryption:</strong> Data in transit is encrypted via SSL/TLS. 
                            Sensitive keys and tokens are stored securely in backend environment variables.
                        </li>
                        <li>
                            <strong>Infrastructure:</strong> Our database and authentication services are hosted on Google Firebase, 
                            leveraging Google's world-class security infrastructure.
                        </li>
                        <li>
                            <strong>Access Control:</strong> Access to user data is restricted to authorized personnel for debugging and support purposes only.
                        </li>
                    </ul>
                </Section>

                {/* 6. Cookies */}
                <Section id="cookies" title="6. Cookies & Local Storage">
                    <p>
                        We use cookies and local storage to maintain your session and preferences.
                    </p>
                    <div className="mt-4 space-y-3">
                        <div className="flex gap-3 text-sm">
                            <span className="w-24 font-mono text-slate-500 shrink-0">Essential</span>
                            <span className="text-slate-300">Required for login authentication (Firebase) and security tokens.</span>
                        </div>
                        <div className="flex gap-3 text-sm">
                            <span className="w-24 font-mono text-slate-500 shrink-0">Analytics</span>
                            <span className="text-slate-300">We may use anonymous analytics to improve platform performance.</span>
                        </div>
                    </div>
                </Section>

                {/* 7. Your Rights */}
                <Section id="rights" title="7. Your Rights">
                    <p>
                        Depending on your location, you have rights regarding your personal data:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-slate-400 mt-2">
                        <li><strong>Access:</strong> You can view your profile data and credit history within the "Settings" page.</li>
                        <li><strong>Correction:</strong> You can update your profile details at any time.</li>
                        <li><strong>Deletion:</strong> You may request the deletion of your account and associated data by contacting support. 
                        Note that transaction records may be retained for legal/tax purposes.</li>
                    </ul>
                </Section>

                {/* 8. Contact */}
                <Section id="contact" title="8. Contact Us">
                    <p>
                        If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
                    </p>
                    <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-6">
                        <div className="flex items-center gap-3 text-white font-medium mb-2">
                            <Mail className="text-blue-400" size={20} /> Email Support
                        </div>
                        <a href="mailto:publicstring.care@gmail.com" className="text-slate-400 hover:text-white transition-colors">
                publicstring.care@gmail.com
                        </a>
                    </div>
                </Section>

            </main>
        </div>
      </div>
    </div>
  );
}

// --- SUB COMPONENTS ---

function Section({ id, title, children }) {
    return (
        <section id={id} className="scroll-mt-32 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                {title}
            </h2>
            <div className="space-y-4 text-slate-400 leading-relaxed text-base">
                {children}
            </div>
        </section>
    );
}

function NavLink({ id, icon: Icon, label, active, onClick }) {
    const isActive = active === id;
    return (
        <button
            onClick={() => onClick(id)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive 
                ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' 
                : 'text-slate-400 hover:bg-slate-900 hover:text-white border border-transparent'
            }`}
        >
            <div className="flex items-center gap-3">
                <Icon size={16} className={isActive ? 'text-blue-400' : 'text-slate-500'} />
                {label}
            </div>
            {isActive && <ChevronRight size={14} className="animate-in slide-in-from-left-2" />}
        </button>
    );
}
